import {warn} from 'util/Logging';

import {ProcessFlowchartNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from './PluginRegistry';

/**
 * Base class for application team extensions to the Processes editor.
 *
 * Application teams subclass this base type to specify the extensions they're making available for use
 * with the editor. The abstract properties on this type specify different kinds of extensions.
 *
 * Clients wishing to use the Processes editor (see ProcessFlowchartConfigEditor) with extensions should
 * import the plugins containing those extensions, instantiate them, and call registerForEditor() to
 * make extensions provided by those plugins available to the editor. It's best to do this outside
 * the React component using the editor. For example:
 *
 * ```tsx
 * const workflowsPlugin = new DefaultProcessesPlugin();
 * workflowsPlugin.registerForEditor();
 *
 * const myPlugin = new MyPlugin();
 * myPlugin.registerForEditor();
 *
 * function MyComponentWithEditor(props: MyComponentWithEditorProps): JSX.Element {
 *   // The editor will now have access to the extensions provided by the loaded plugins.
 *   return <ProcessFlowchartConfigEditor .../>;
 * }
 * ```
 *
 * See this confluence page for more information on plugins: https://benchling.atlassian.net/wiki/spaces/Workflows/pages/1285619729/Adding+A+Processes+Plugin
 *
 * Default plugins are registered during test setup in client/test/lib/testSetup.ts. If you are adding a new plugin, you may need to update test setup accordingly.
 */
export abstract class ProcessesPluginBase {
  /**
   * A unique name for this plugin.
   */
  abstract readonly pluginName: string;

  /**
   * The node adapters provided by this plugin.
   *
   * Node adapters tell the editor how to work with nodes for a particular task type, e.g. how to render
   * the node on the flowchart canvas and what sidepanel configuration component to show for the node.
   */
  abstract readonly nodeAdapters: Array<ProcessFlowchartNodeAdapter>;

  /**
   * Register this plugin and its extensions with the editor.
   *
   * Subclasses should never override this method.
   */
  registerForEditor(): void {
    if (!PLUGIN_REGISTRY_INSTANCE.hasPlugin(this.pluginName)) {
      PLUGIN_REGISTRY_INSTANCE.registerPluginAndItsExtensions(this);
    } else {
      warn(`Plugin ${this.pluginName} is already registered. Skipping registration.`);
    }
  }
}
